import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Mail, Send, Loader2, Info, Server, KeyRound, CheckCircle, AlertCircle } from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { admin } from '@/lib/api';
import { toast } from 'sonner';
import { useAuth } from '@/lib/auth-context';

const MailSettingsTab = () => {
  const { user } = useAuth();
  const testEmailInited = useRef(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [info, setInfo] = useState(null);
  const [form, setForm] = useState({
    mail_provider: 'mailjet',
    sender_email: '',
    sender_name: '',
    reply_to_email: '',
    smtp_host: '',
    smtp_port: '587',
    smtp_username: '',
    smtp_use_tls: true,
    smtp_enabled: false,
  });
  const [mailjetApiKey, setMailjetApiKey] = useState('');
  const [mailjetSecret, setMailjetSecret] = useState('');
  const [smtpPassword, setSmtpPassword] = useState('');
  const [touchMailjetKey, setTouchMailjetKey] = useState(false);
  const [touchMailjetSecret, setTouchMailjetSecret] = useState(false);
  const [touchSmtpPassword, setTouchSmtpPassword] = useState(false);
  const [testEmail, setTestEmail] = useState('');
  const [testing, setTesting] = useState(false);
  /** { ok, message, detail? } | null */
  const [testFeedback, setTestFeedback] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await admin.getMailSettings();
      setInfo(data);
      const s = data.stored || {};
      const mp = (s.mail_provider || data.mail_provider || 'mailjet').toString().toLowerCase();
      setForm({
        mail_provider: mp === 'smtp' ? 'smtp' : 'mailjet',
        sender_email: s.sender_email || data.effective?.sender_email || '',
        sender_name: s.sender_name || data.effective?.sender_name || '',
        reply_to_email: s.reply_to_email || '',
        smtp_host: s.smtp_host || '',
        smtp_port: String(s.smtp_port ?? 587),
        smtp_username: s.smtp_username || '',
        smtp_use_tls: s.smtp_use_tls !== false,
        smtp_enabled: !!s.smtp_enabled,
      });
      setMailjetApiKey('');
      setMailjetSecret('');
      setSmtpPassword('');
      setTouchMailjetKey(false);
      setTouchMailjetSecret(false);
      setTouchSmtpPassword(false);
    } catch (e) {
      console.error(e);
      toast.error('Mail ayarları yüklenemedi');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    if (testEmailInited.current) return;
    if (user?.email) {
      setTestEmail((e) => e || user.email);
      testEmailInited.current = true;
    }
  }, [user]);

  const handleSave = async () => {
    setSaving(true);
    try {
      const payload = {
        mail_provider: form.mail_provider,
        sender_email: form.sender_email,
        sender_name: form.sender_name,
        reply_to_email: form.reply_to_email,
        smtp_host: form.smtp_host,
        smtp_port: parseInt(form.smtp_port, 10) || 587,
        smtp_username: form.smtp_username,
        smtp_use_tls: form.smtp_use_tls,
        smtp_enabled: form.smtp_enabled,
      };
      if (touchMailjetKey) payload.mailjet_api_key = mailjetApiKey.trim();
      if (touchMailjetSecret) payload.mailjet_secret_key = mailjetSecret.trim();
      if (touchSmtpPassword) payload.smtp_password = smtpPassword.trim();

      const { data } = await admin.updateMailSettings(payload);
      toast.success(data.message || 'Kaydedildi');
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Kayıt başarısız');
    } finally {
      setSaving(false);
    }
  };

  const clearDbMailjetKeys = async () => {
    setSaving(true);
    try {
      const { data } = await admin.updateMailSettings({
        mailjet_api_key: '',
        mailjet_secret_key: '',
      });
      toast.success(data.message || 'Veritabanı anahtarları silindi');
      setMailjetApiKey('');
      setMailjetSecret('');
      setTouchMailjetKey(false);
      setTouchMailjetSecret(false);
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'İşlem başarısız');
    } finally {
      setSaving(false);
    }
  };

  const formatApiError = (e) => {
    const d = e.response?.data;
    if (!d) return e.message || String(e);
    if (typeof d === 'string') return d;
    if (d.detail) {
      if (Array.isArray(d.detail)) {
        return d.detail.map((x) => (typeof x === 'object' ? JSON.stringify(x) : String(x))).join(' ');
      }
      return String(d.detail);
    }
    return JSON.stringify(d, null, 2);
  };

  const sendTest = async () => {
    if (!testEmail.trim()) {
      setTestFeedback({
        ok: false,
        message: 'E-posta eksik',
        detail: 'Test göndermek için geçerli bir alıcı e-posta adresi girin.',
      });
      toast.error('Test e-posta adresi girin');
      return;
    }
    setTesting(true);
    setTestFeedback(null);
    try {
      const { data } = await admin.testEmail({ email: testEmail.trim() });
      if (data.success) {
        setTestFeedback({
          ok: true,
          message: data.message || 'E-posta kuyruğa alındı. Gelen kutunuzu (ve gerekiyorsa spam) kontrol edin.',
        });
        toast.success('Test e-postası gönderildi');
      } else {
        const parts = [data.error, data.error_detail].filter(Boolean);
        let detail = parts.join(' — ') || 'Bilinmeyen hata';
        if (data.mailjet_response != null) {
          try {
            const extra = typeof data.mailjet_response === 'object'
              ? JSON.stringify(data.mailjet_response, null, 2)
              : String(data.mailjet_response);
            detail = `${detail}\n\nMailjet yanıtı:\n${extra}`;
          } catch {
            // ignore
          }
        } else if (data.response != null) {
          try {
            detail = `${detail}\n\nYanıt:\n${JSON.stringify(data.response, null, 2)}`;
          } catch {
            // ignore
          }
        }
        setTestFeedback({
          ok: false,
          message: data.error || 'Gönderim başarısız',
          detail,
        });
        toast.error('Test e-postası gönderilemedi');
      }
    } catch (e) {
      const detail = formatApiError(e);
      setTestFeedback({
        ok: false,
        message: 'Sunucu veya ağ hatası',
        detail,
      });
      toast.error('İstek başarısız');
    } finally {
      setTesting(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-24 text-slate-500">
        <Loader2 className="w-8 h-8 animate-spin mr-2" />
        Yükleniyor…
      </div>
    );
  }

  const eff = info?.effective || {};
  const src = info?.sources || {};

  return (
    <div className="max-w-3xl space-y-6">
      <div>
        <h1 className="text-2xl font-serif text-slate-900 flex items-center gap-2">
          <Mail className="w-7 h-7 text-red-600" />
          Mail yönetimi
        </h1>
        <p className="text-slate-600 mt-1 text-sm">
          Gönderim yöntemini (Mailjet veya SMTP) buradan seçin. Anahtarlar veritabanında veya sunucu ortamında (.env) tutulabilir; paneldeki değerler ortam değişkeninin üzerine yazar.
        </p>
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="text-lg">Gönderim yöntemi</CardTitle>
          <CardDescription>
            Canlı gönderim hangi kanaldan yapılacağını seçin. SMTP için sunucu, kullanıcı ve şifre zorunludur.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <RadioGroup
            value={form.mail_provider}
            onValueChange={(v) => setForm((f) => ({ ...f, mail_provider: v }))}
            className="space-y-3"
          >
            <div className="flex items-start space-x-3 rounded-lg border border-slate-200 p-3">
              <RadioGroupItem value="mailjet" id="mp-mailjet" />
              <div>
                <Label htmlFor="mp-mailjet" className="font-medium cursor-pointer">
                  Mailjet (REST API)
                </Label>
                <p className="text-xs text-slate-600 mt-1">API Key + Secret Key gerekir.</p>
              </div>
            </div>
            <div className="flex items-start space-x-3 rounded-lg border border-slate-200 p-3">
              <RadioGroupItem value="smtp" id="mp-smtp" />
              <div>
                <Label htmlFor="mp-smtp" className="font-medium cursor-pointer">
                  SMTP
                </Label>
                <p className="text-xs text-slate-600 mt-1">Kendi sunucunuz (ör. 587 STARTTLS veya 465 SSL).</p>
              </div>
            </div>
          </RadioGroup>
        </CardContent>
      </Card>

      <Card className="border-2 border-amber-200 bg-amber-50/30">
        <CardHeader>
          <CardTitle className="text-lg flex items-center gap-2">
            <Send className="w-5 h-5 text-amber-600" />
            Test e-postası
          </CardTitle>
          <CardDescription>
            Kendi e-postanızı veya test kutunuzu girin, <strong>Test gönder</strong> deyin. Gelen kutunuzda &quot;Turkish E-Market - Test Email&quot; konu satırını arayın. Hata olursa aşağıda ayrıntı görünür.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex flex-col sm:flex-row sm:flex-wrap gap-3 sm:items-end">
            <div className="flex-1 min-w-[220px] space-y-2">
              <Label htmlFor="test-mail-input">Test alıcı e-posta</Label>
              <Input
                id="test-mail-input"
                data-testid="mail-test-recipient"
                value={testEmail}
                onChange={(e) => {
                  setTestEmail(e.target.value);
                  setTestFeedback(null);
                }}
                type="email"
                placeholder="ornek@sizinmail.com"
                autoComplete="email"
              />
            </div>
            <Button
              type="button"
              onClick={sendTest}
              disabled={testing}
              className="bg-amber-600 hover:bg-amber-700 text-white w-full sm:w-auto"
              data-testid="mail-test-send"
            >
              {testing ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Send className="w-4 h-4 mr-2" />}
              Test e-postası gönder
            </Button>
          </div>

          {testFeedback && (
            <div
              role="status"
              className={
                testFeedback.ok
                  ? 'rounded-lg border border-emerald-200 bg-emerald-50/90 p-4 text-sm'
                  : 'rounded-lg border border-red-200 bg-red-50/90 p-4 text-sm'
              }
              data-testid="mail-test-feedback"
            >
              {testFeedback.ok ? (
                <div className="flex gap-2 text-emerald-900">
                  <CheckCircle className="w-5 h-5 flex-shrink-0 text-emerald-600 mt-0.5" />
                  <div>
                    <p className="font-medium">Başarılı</p>
                    <p className="text-emerald-800 mt-1">{testFeedback.message}</p>
                  </div>
                </div>
              ) : (
                <div className="flex gap-2 text-red-900">
                  <AlertCircle className="w-5 h-5 flex-shrink-0 text-red-600 mt-0.5" />
                  <div className="min-w-0 flex-1">
                    <p className="font-medium">Gönderilemedi</p>
                    <p className="text-red-800 mt-1">{testFeedback.message}</p>
                    {testFeedback.detail && (
                      <pre className="mt-3 p-3 rounded bg-white/80 border border-red-100 text-xs text-red-950 whitespace-pre-wrap break-words max-h-64 overflow-y-auto">
                        {testFeedback.detail}
                      </pre>
                    )}
                  </div>
                </div>
              )}
            </div>
          )}
        </CardContent>
      </Card>

      <Card className="border-emerald-200 bg-emerald-50/60">
        <CardHeader className="pb-2">
          <CardTitle className="text-base flex items-center gap-2">
            <Info className="w-5 h-5" />
            Şu an kullanılan / Current stack
          </CardTitle>
          <CardDescription className="text-slate-700 space-y-1">
            <p>
              <strong>Gönderim:</strong>{' '}
              {(info?.mail_provider || form.mail_provider) === 'smtp' ? (
                <>
                  SMTP — <code className="bg-white/80 px-1 rounded">smtp</code>
                </>
              ) : (
                <>
                  Mailjet (REST API v3.1) — <code className="bg-white/80 px-1 rounded">mailjet_rest</code>
                </>
              )}
            </p>
            <p>
              <strong>Durum:</strong>{' '}
              {eff.outbound_ready ? (
                <span className="text-emerald-700 font-medium">Gönderim hazır</span>
              ) : (info?.mail_provider || form.mail_provider) === 'smtp' ? (
                <span className="text-amber-700 font-medium">SMTP sunucu / kullanıcı / şifre eksik</span>
              ) : eff.mailjet_ready ? (
                <span className="text-emerald-700 font-medium">Mailjet hazır</span>
              ) : (
                <span className="text-amber-700 font-medium">Mailjet anahtarları eksik — e-posta gönderilmez</span>
              )}
            </p>
            <p>
              <strong>Gönderen:</strong> {eff.sender_name} &lt;{eff.sender_email}&gt;
            </p>
            <p className="text-xs mt-2">{info?.note_tr}</p>
          </CardDescription>
        </CardHeader>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-lg">
            <KeyRound className="w-5 h-5" />
            Mailjet API
          </CardTitle>
          <CardDescription>
            API Key / Secret Key (Mailjet hesabı). Kaynak:{' '}
            <span className="font-mono text-xs bg-slate-100 px-1 rounded">
              API: {src.mailjet_api_key} / Secret: {src.mailjet_secret_key}
            </span>
            {info?.secrets?.mailjet_api_key_effective_mask && (
              <span className="block mt-1 text-xs">
                Etkin anahtar maskesi: {info.secrets.mailjet_api_key_effective_mask}
              </span>
            )}
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="mj-api">Mailjet API Key</Label>
            <Input
              id="mj-api"
              type="password"
              autoComplete="off"
              placeholder="Değiştirmek için yeni değeri girin (boş bırakırsanız mevcut korunur)"
              value={mailjetApiKey}
              onChange={(e) => {
                setMailjetApiKey(e.target.value);
                setTouchMailjetKey(true);
              }}
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="mj-sec">Mailjet Secret Key</Label>
            <Input
              id="mj-sec"
              type="password"
              autoComplete="off"
              placeholder="Değiştirmek için yeni değeri girin"
              value={mailjetSecret}
              onChange={(e) => {
                setMailjetSecret(e.target.value);
                setTouchMailjetSecret(true);
              }}
            />
          </div>
          <Button type="button" variant="outline" size="sm" onClick={clearDbMailjetKeys} disabled={saving}>
            Veritabanındaki Mailjet anahtarlarını sil (.env kullan)
          </Button>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="text-lg">Gönderen adresi</CardTitle>
          <CardDescription>From e-posta ve görünen ad (çoğu şablonda kullanılır).</CardDescription>
        </CardHeader>
        <CardContent className="grid gap-4 sm:grid-cols-2">
          <div className="space-y-2 sm:col-span-2">
            <Label>Gönderen e-posta</Label>
            <Input
              value={form.sender_email}
              onChange={(e) => setForm((f) => ({ ...f, sender_email: e.target.value }))}
              placeholder="noreply@alanadiniz.com"
            />
          </div>
          <div className="space-y-2">
            <Label>Görünen ad</Label>
            <Input
              value={form.sender_name}
              onChange={(e) => setForm((f) => ({ ...f, sender_name: e.target.value }))}
            />
          </div>
          <div className="space-y-2">
            <Label>Reply-To (isteğe bağlı)</Label>
            <Input
              value={form.reply_to_email}
              onChange={(e) => setForm((f) => ({ ...f, reply_to_email: e.target.value }))}
              placeholder="destek@..."
            />
          </div>
        </CardContent>
      </Card>

      <Card className={form.mail_provider === 'smtp' ? 'border-primary/40' : 'border-dashed'}>
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-lg">
            <Server className="w-5 h-5" />
            SMTP
          </CardTitle>
          <CardDescription className="space-y-1">
            <p>
              «Gönderim yöntemi» SMTP iken posta <strong>burada kayıtlı</strong> sunucu / kullanıcı / şifre ile gider. Port genelde 587 (STARTTLS) veya 465 (SSL).
            </p>
            <p className="text-amber-800 dark:text-amber-200/90">
              SMTP şifresi <strong>sadece</strong> bu alana yazıp <strong>Kaydet</strong> dediğinizde veritabanına yazılır ve gönderimde kullanılır; .env içindeki{' '}
              <code className="text-xs bg-white/80 dark:bg-slate-800 px-1 rounded">SMTP_PASSWORD</code> bu gönderimler için artık kullanılmaz.
            </p>
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center justify-between">
            <Label>SMTP sekmesini vurgula (kayıt)</Label>
            <Switch
              checked={form.smtp_enabled}
              onCheckedChange={(v) => setForm((f) => ({ ...f, smtp_enabled: v }))}
            />
          </div>
          <div className="grid gap-4 sm:grid-cols-2">
            <div className="space-y-2">
              <Label>SMTP sunucusu</Label>
              <Input
                value={form.smtp_host}
                onChange={(e) => setForm((f) => ({ ...f, smtp_host: e.target.value }))}
                placeholder="smtp.ornek.com"
              />
            </div>
            <div className="space-y-2">
              <Label>Port</Label>
              <Input
                type="number"
                value={form.smtp_port}
                onChange={(e) => setForm((f) => ({ ...f, smtp_port: e.target.value }))}
              />
            </div>
            <div className="space-y-2">
              <Label>Kullanıcı adı</Label>
              <Input
                value={form.smtp_username}
                onChange={(e) => setForm((f) => ({ ...f, smtp_username: e.target.value }))}
              />
            </div>
            <div className="space-y-2 sm:col-span-2">
              <Label>SMTP şifresi</Label>
              {info?.secrets?.smtp_password_set && !touchSmtpPassword ? (
                <p className="text-xs text-slate-600">
                  Kayıtlı şifre kullanılıyor. Değiştirmek veya sıfırdan girmek için yeni şifreyi yazıp Kaydet; kaldırmak için alanı temizleyip Kaydet.
                </p>
              ) : null}
              <Input
                type="password"
                autoComplete="new-password"
                placeholder="Şifreyi buraya yazın ve Kaydet — gönderim bu kayıtlı şifreyle yapılır"
                value={smtpPassword}
                onChange={(e) => {
                  setSmtpPassword(e.target.value);
                  setTouchSmtpPassword(true);
                }}
              />
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Switch
              id="tls"
              checked={form.smtp_use_tls}
              onCheckedChange={(v) => setForm((f) => ({ ...f, smtp_use_tls: v }))}
            />
            <Label htmlFor="tls">TLS / STARTTLS</Label>
          </div>
        </CardContent>
      </Card>

      <div className="flex flex-wrap gap-3">
        <Button onClick={handleSave} disabled={saving} className="bg-red-600 hover:bg-red-700">
          {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
          Kaydet
        </Button>
      </div>
    </div>
  );
};

export default MailSettingsTab;
